import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  CircularProgress, Chip, Typography
} from '@material-ui/core'

const SCOPE_TYPES = ['material', 'service', 'subcontract', 'allowance', 'combination']
const SCOPE_COLORS = {
  material:     { bg: '#e8f5e9', color: '#2e7d32' },
  service:      { bg: '#e3f2fd', color: '#1565c0' },
  subcontract:  { bg: '#fce4ec', color: '#c62828' },
  allowance:    { bg: '#fff3e0', color: '#ef6c00' },
  combination:  { bg: '#f3e5f5', color: '#6a1b9a' }
}

const th = {
  padding: '8px 10px', textAlign: 'left', fontWeight: 600,
  position: 'sticky', top: 0, zIndex: 2,
  background: 'var(--bg-panel-alt, #faf8f3)',
  borderBottom: '2px solid var(--line-2, #ddd)'
}

const ImportedItemsModal = ({ projectId, documentId, documentName, url, onClose }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState({})
  const [selected, setSelected] = useState(new Set())
  const [bulkSaving, setBulkSaving] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [promoteResult, setPromoteResult] = useState(null)

  useEffect(() => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/line-items?sourceDocumentId=${documentId}`)
      .then(res => { setItems(res.data || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [projectId, documentId, url])

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set())
    else setSelected(new Set(items.map(it => it._id)))
  }

  const handleScopeChange = async (itemId, scopeType) => {
    setSaving(prev => ({ ...prev, [itemId]: true }))
    try {
      const res = await axios.put(`${url}/api/bid-estimation/line-items/${itemId}`, { scopeType: scopeType || null })
      setItems(prev => prev.map(it => it._id === itemId ? { ...it, scopeType: res.data.scopeType } : it))
    } catch (e) {
      console.error('Failed to update scopeType:', e)
    }
    setSaving(prev => ({ ...prev, [itemId]: false }))
  }

  const handleSectionChange = async (itemId, section) => {
    setSaving(prev => ({ ...prev, [itemId]: true }))
    try {
      const res = await axios.put(`${url}/api/bid-estimation/line-items/${itemId}`, { section })
      setItems(prev => prev.map(it => it._id === itemId ? { ...it, section: res.data.section } : it))
    } catch (e) {
      console.error('Failed to update section:', e)
    }
    setSaving(prev => ({ ...prev, [itemId]: false }))
  }

  const handleBulkScope = async (scopeType) => {
    if (selected.size === 0) return
    setBulkSaving(true)
    const itemIds = [...selected]
    try {
      const res = await axios.put(`${url}/api/bid-estimation/line-items/bulk-update`, {
        itemIds,
        updates: { scopeType: scopeType || null }
      })
      const updatedMap = {}
      for (const it of res.data) updatedMap[it._id] = it
      setItems(prev => prev.map(it => updatedMap[it._id] ? { ...it, ...updatedMap[it._id] } : it))
    } catch (e) {
      console.error('Bulk scope update failed:', e)
    }
    setBulkSaving(false)
  }

  const handleBulkSection = async (section) => {
    if (selected.size === 0) return
    setBulkSaving(true)
    const itemIds = [...selected]
    try {
      const res = await axios.put(`${url}/api/bid-estimation/line-items/bulk-update`, {
        itemIds,
        updates: { section }
      })
      const updatedMap = {}
      for (const it of res.data) updatedMap[it._id] = it
      setItems(prev => prev.map(it => updatedMap[it._id] ? { ...it, ...updatedMap[it._id] } : it))
    } catch (e) {
      console.error('Bulk section update failed:', e)
    }
    setBulkSaving(false)
  }

  const handlePromoteToMaterials = async () => {
    setPromoting(true)
    setPromoteResult(null)
    try {
      const res = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/line-items/promote-materials`,
        { sourceDocumentId: documentId }
      )
      setPromoteResult(res.data)
    } catch (e) {
      console.error('Promote to materials failed:', e)
      setPromoteResult({ error: e.response?.data?.error || e.message })
    }
    setPromoting(false)
  }

  const counts = {
    total: items.length,
    material: items.filter(it => it.scopeType === 'material').length,
    service: items.filter(it => it.scopeType === 'service').length,
    untagged: items.filter(it => !it.scopeType).length
  }

  const allChecked = items.length > 0 && selected.size === items.length
  const someChecked = selected.size > 0 && selected.size < items.length

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1300, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-panel, #fff)', borderRadius: 12,
        width: '95vw', maxWidth: 1200, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--line-2, #ddd)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Imported Items</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{documentName}</div>
            </div>
            <button
              className="hmi-btn hmi-btn-sm hmi-btn-ghost"
              onClick={onClose}
              style={{ fontSize: 18, padding: '4px 8px' }}
            >×</button>
          </div>
          {!loading && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip size="small" label={`${counts.total} total`} style={{ fontSize: 10 }} />
              <Chip size="small" label={`${counts.material} material`} style={{ fontSize: 10, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
              <Chip size="small" label={`${counts.service} service`} style={{ fontSize: 10, backgroundColor: '#e3f2fd', color: '#1565c0' }} />
              {counts.untagged > 0 && (
                <Chip size="small" label={`${counts.untagged} untagged`} style={{ fontSize: 10, backgroundColor: '#fff3e0', color: '#e65100' }} />
              )}
              <div style={{ flex: 1 }} />
              <button
                className="hmi-btn hmi-btn-sm hmi-btn-primary"
                disabled={promoting || counts.material === 0}
                onClick={handlePromoteToMaterials}
                style={{ fontSize: 11, padding: '4px 12px' }}
              >
                {promoting ? 'Promoting...' : `Promote ${counts.material} materials →`}
              </button>
            </div>
          )}
          {promoteResult && !promoteResult.error && (
            <div style={{
              marginTop: 8, padding: '6px 12px', borderRadius: 6, fontSize: 11,
              background: '#e8f5e9', color: '#2e7d32'
            }}>
              Created {promoteResult.created} material{promoteResult.created !== 1 ? 's' : ''}
              {promoteResult.updated > 0 && `, updated ${promoteResult.updated} with quantities`}
              {promoteResult.skipped > 0 && ` (${promoteResult.skipped} already existed)`}
              {' — view them on the Materials tab'}
            </div>
          )}
          {promoteResult && promoteResult.error && (
            <div style={{
              marginTop: 8, padding: '6px 12px', borderRadius: 6, fontSize: 11,
              background: '#fce4ec', color: '#c62828'
            }}>
              Failed: {promoteResult.error}
            </div>
          )}

          {/* Bulk actions bar */}
          {selected.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginTop: 10,
              padding: '8px 12px', borderRadius: 8,
              background: 'var(--bg-sunken, #f0ede6)', fontSize: 12
            }}>
              <span style={{ fontWeight: 600 }}>{selected.size} selected</span>
              <span style={{ color: 'var(--ink-4)' }}>|</span>
              <span style={{ color: 'var(--ink-3)' }}>Set scope:</span>
              {SCOPE_TYPES.map(t => {
                const sc = SCOPE_COLORS[t]
                return (
                  <button
                    key={t}
                    className="hmi-btn hmi-btn-sm"
                    disabled={bulkSaving}
                    onClick={() => handleBulkScope(t)}
                    style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 10,
                      background: sc.bg, color: sc.color, fontWeight: 600,
                      border: 'none', cursor: 'pointer'
                    }}
                  >{t}</button>
                )
              })}
              <button
                className="hmi-btn hmi-btn-sm hmi-btn-ghost"
                disabled={bulkSaving}
                onClick={() => handleBulkScope(null)}
                style={{ fontSize: 10, padding: '2px 8px' }}
              >Clear</button>

              <span style={{ color: 'var(--ink-4)' }}>|</span>
              <span style={{ color: 'var(--ink-3)' }}>Set section:</span>
              <input
                type="text"
                placeholder="Section name…"
                style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 4, width: 120,
                  border: '1px solid var(--line-2, #ccc)'
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    handleBulkSection(e.target.value.trim())
                    e.target.value = ''
                  }
                }}
              />
              {bulkSaving && <CircularProgress size={14} />}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress />
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
              <Typography variant="body2">No imported items found for this document.</Typography>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 32, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked }}
                      onChange={toggleAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ ...th, width: 40 }}>#</th>
                  <th style={th}>Description</th>
                  <th style={{ ...th, width: 55 }}>Unit</th>
                  <th style={{ ...th, width: 70, textAlign: 'right' }}>Qty</th>
                  <th style={{ ...th, width: 160 }}>Notes</th>
                  <th style={{ ...th, width: 90 }}>Section</th>
                  <th style={{ ...th, width: 130, textAlign: 'center' }}>Scope Type</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => {
                  const sc = SCOPE_COLORS[item.scopeType] || null
                  const isSelected = selected.has(item._id)
                  const notes = item.notes || item.rawNotesText || ''
                  return (
                    <tr
                      key={item._id}
                      onClick={() => toggleSelect(item._id)}
                      style={{
                        borderBottom: '1px solid var(--line-2, #eee)',
                        background: isSelected
                          ? 'var(--brand-tint, #e8f0eb)'
                          : i % 2 ? 'var(--bg-panel-alt, #faf8f3)' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{ padding: '6px 10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(item._id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}>{item.itemNo || '—'}</td>
                      <td style={{ padding: '6px 10px' }}>{item.description}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--ink-3)' }}>{item.unit}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--f-mono)' }}>{item.quantity || '—'}</td>
                      <td style={{ padding: '6px 10px', color: 'var(--ink-3)', fontSize: 10.5, maxWidth: 160, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {notes || '—'}
                      </td>
                      <td style={{ padding: '6px 10px' }} onClick={e => e.stopPropagation()}>
                        {saving[item._id] ? (
                          <CircularProgress size={12} />
                        ) : (
                          <input
                            key={`${item._id}-${item.section || ''}`}
                            type="text"
                            defaultValue={item.section || ''}
                            onBlur={e => {
                              const val = e.target.value.trim()
                              if (val !== (item.section || '')) handleSectionChange(item._id, val)
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') e.target.blur()
                            }}
                            style={{
                              fontSize: 11, padding: '2px 6px', borderRadius: 4, width: '100%',
                              border: '1px solid var(--line-2, #ccc)', background: 'transparent'
                            }}
                          />
                        )}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        {saving[item._id] ? (
                          <CircularProgress size={14} />
                        ) : (
                          <select
                            value={item.scopeType || ''}
                            onChange={e => handleScopeChange(item._id, e.target.value)}
                            style={{
                              fontSize: 11, padding: '3px 6px', borderRadius: 4,
                              border: '1px solid var(--line-2, #ccc)',
                              background: sc ? sc.bg : 'transparent',
                              color: sc ? sc.color : 'inherit',
                              fontWeight: sc ? 600 : 400, cursor: 'pointer',
                              width: '100%'
                            }}
                          >
                            <option value="">— select —</option>
                            {SCOPE_TYPES.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImportedItemsModal
